import Cookies from 'js-cookie';

import { getAuthBaseUrl, getOAuthAppId, getOAuthClientId, getOAuthRedirectUri } from '@deriv/shared';

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

const generateCodeVerifier = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
};

const PKCE_VERIFIER_KEY = 'oauth_code_verifier';
const PKCE_EXPIRY_KEY = 'oauth_code_verifier_timestamp';
const PKCE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export const storePKCEVerifier = (verifier: string): void => {
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(PKCE_EXPIRY_KEY, String(Date.now()));
};

export const getPKCEVerifier = (): string | null => {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const timestamp = Number(sessionStorage.getItem(PKCE_EXPIRY_KEY) ?? 0);
    if (!verifier || Date.now() - timestamp > PKCE_TTL_MS) {
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_EXPIRY_KEY);
        return null;
    }
    return verifier;
};

export const clearPKCEVerifier = (): void => {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_EXPIRY_KEY);
};

// ---------------------------------------------------------------------------
// OAuth URL generation
// ---------------------------------------------------------------------------

export const generateOAuthURL = async (): Promise<string> => {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    storePKCEVerifier(verifier);

    const csrf_token = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16))));
    sessionStorage.setItem('oauth_csrf_token', csrf_token);

    const client_id = getOAuthClientId();
    const redirect_uri = getOAuthRedirectUri();

    try {
        sessionStorage.setItem('oauth_client_id', client_id);
        sessionStorage.setItem('oauth_redirect_uri', redirect_uri);
    } catch {
        /* ignore quota / private mode */
    }

    const params = new URLSearchParams({
        response_type: 'code',
        client_id,
        redirect_uri,
        scope: 'trade',
        state: csrf_token,
        code_challenge: challenge,
        code_challenge_method: 'S256',
    });
    params.set('app_id', getOAuthAppId());

    return `${getAuthBaseUrl()}/oauth2/auth?${params}`;
};

// ---------------------------------------------------------------------------
// Token exchange
// ---------------------------------------------------------------------------

export const exchangeCodeForToken = async (
    code: string
): Promise<{ access_token: string; refresh_token?: string; expires_in?: number }> => {
    const verifier = getPKCEVerifier();
    if (!verifier) throw new Error('PKCE code verifier not found or expired — restart login flow');

    const response = await fetch(`${getAuthBaseUrl()}/oauth2/token`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            code,
            redirect_uri: getOAuthRedirectUri(),
            client_id: getOAuthClientId(),
            code_verifier: verifier,
        }),
    });

    if (!response.ok) throw new Error(`Token exchange failed: ${response.status}`);

    const data = await response.json();
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[OAuth] Token exchange response:', {
            scope: data.scope,
            token_type: data.token_type,
            expires_in: data.expires_in,
        });
    }
    if (!data.access_token)
        throw new Error(`Token exchange succeeded but no access_token in response: ${JSON.stringify(data)}`);
    clearPKCEVerifier();
    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    return data;
};

// ---------------------------------------------------------------------------
// Token storage
// Access and refresh tokens go in sessionStorage — cleared on tab close.
// active_loginid stays in localStorage for multi-tab account awareness.
// ---------------------------------------------------------------------------

const AUTH_INFO_KEY = 'auth_info';

/** Same markers as profitap analyst — SSO helpers and refresh flows expect these. Host-only cookie (no parent domain) so e.g. *.netlify.app sites do not share state. */
const setLoggedStateCookies = (): void => {
    if (typeof window === 'undefined') return;
    Cookies.set('logged_state', 'true', {
        expires: 30,
        path: '/',
        secure: window.location.protocol === 'https:',
    });
};

type TAuthInfo = {
    access_token?: string;
    refresh_token?: string | null;
    expires_at?: number | null;
};

/** Refresh slightly before wall-clock expiry to avoid racing the first API call. */
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000;

const readAuthInfo = (): TAuthInfo | null => {
    try {
        return JSON.parse(sessionStorage.getItem(AUTH_INFO_KEY) ?? 'null');
    } catch {
        return null;
    }
};

export const storeTokens = (access_token: string, refresh_token?: string | null, expires_in?: number): void => {
    // Preserve the existing refresh token when the token endpoint omits a new one.
    const existing_refresh = readAuthInfo()?.refresh_token;
    sessionStorage.setItem(
        AUTH_INFO_KEY,
        JSON.stringify({
            access_token,
            refresh_token: refresh_token || existing_refresh || null,
            expires_at: typeof expires_in === 'number' ? Date.now() + expires_in * 1000 : null,
        })
    );
    sessionStorage.setItem('auth_mode', 'v2');
    setLoggedStateCookies();
};

/** OAuth2 bearer from auth.deriv.com (ory_at_…) — must use v4 OTP WS, not v3 `{ authorize }`. */
export const isOAuthBearerToken = (token: string | null | undefined): boolean => {
    if (!token || typeof token !== 'string') return false;
    if (token.startsWith('ory_at_')) return true;
    try {
        return sessionStorage.getItem('auth_mode') === 'v2';
    } catch {
        return false;
    }
};

/**
 * Returns the stored access token if present.
 * Does NOT clear storage on expiry — a refresh_token may still restore the session.
 * Use ensureValidAccessToken() before API calls that need a live bearer.
 */
export const getStoredToken = (): string | null => {
    const info = readAuthInfo();
    return info?.access_token ?? null;
};

export const isAccessTokenExpired = (skew_ms = TOKEN_EXPIRY_SKEW_MS): boolean => {
    const info = readAuthInfo();
    if (!info?.access_token) return true;
    if (!info.expires_at) return false;
    return Date.now() >= Number(info.expires_at) - skew_ms;
};

export const clearTokens = (): void => {
    sessionStorage.removeItem(AUTH_INFO_KEY);
    sessionStorage.removeItem('auth_mode');
    if (typeof window !== 'undefined') {
        Cookies.remove('logged_state', { path: '/' });
    }
};

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------

/** Deduplicate concurrent refresh calls (visibility + API 401 + init). */
let refresh_in_flight: Promise<string> | null = null;

export const refreshAccessToken = async (): Promise<string> => {
    if (refresh_in_flight) return refresh_in_flight;

    refresh_in_flight = (async () => {
        const info = readAuthInfo();
        if (!info?.refresh_token) {
            clearTokens();
            throw new Error('No refresh token stored');
        }

        const response = await fetch(`${getAuthBaseUrl()}/oauth2/token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                refresh_token: info.refresh_token,
                client_id: getOAuthClientId(),
            }),
        });

        if (!response.ok) {
            // Refresh token is dead — wipe so UI doesn't stay half-logged-in.
            clearTokens();
            throw new Error(`Token refresh failed: ${response.status}`);
        }

        const data = await response.json();
        if (!data.access_token) {
            clearTokens();
            throw new Error(`Token refresh succeeded but no access_token in response: ${JSON.stringify(data)}`);
        }

        storeTokens(data.access_token, data.refresh_token ?? info.refresh_token, data.expires_in);
        return data.access_token as string;
    })().finally(() => {
        refresh_in_flight = null;
    });

    return refresh_in_flight;
};

/**
 * Returns a usable access token, refreshing when expired (or about to expire).
 * Returns null only when the session cannot be restored — caller should re-login.
 */
export const ensureValidAccessToken = async (): Promise<string | null> => {
    const info = readAuthInfo();
    if (!info?.access_token && !info?.refresh_token) return null;

    if (info.access_token && !isAccessTokenExpired()) {
        return info.access_token;
    }

    if (!info.refresh_token) {
        // Expired (or missing) access token with nothing to refresh — hard stop.
        clearTokens();
        return null;
    }

    try {
        return await refreshAccessToken();
    } catch {
        return null;
    }
};
